import { useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import './Contact.css';

const EASE = [0.16, 1, 0.3, 1];

function MagneticButton({ children, href, className }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 150, damping: 15 });
  const springY = useSpring(y, { stiffness: 150, damping: 15 });
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window);

  function handleMouseMove(e) {
    if (isTouchDevice || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - (rect.left + rect.width / 2)) * 0.15);
    y.set((e.clientY - (rect.top + rect.height / 2)) * 0.15);
  }

  function handleMouseLeave() { x.set(0); y.set(0); }

  return (
    <motion.a
      ref={ref}
      href={href}
      className={className}
      style={{ x: springX, y: springY }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 300, damping: 15 }}
    >
      {children}
    </motion.a>
  );
}

/* ── Apple emoji-style mail icon ── */
function MailIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
      {/* Envelope body */}
      <rect x="6" y="14" width="40" height="28" rx="5" fill="#4A9EF5" />
      <rect x="6" y="14" width="40" height="28" rx="5" fill="url(#mailGrad)" />
      {/* Flap */}
      <path d="M6 18.5C6 16.5 7.5 14 10 14H42C44.5 14 46 16.5 46 18.5L26 32L6 18.5Z" fill="#5AABF7" />
      <path d="M6 18.5C6 16.5 7.5 14 10 14H42C44.5 14 46 16.5 46 18.5L26 32L6 18.5Z" fill="url(#flapGrad)" />
      {/* Flap fold line */}
      <path d="M6 19L26 32L46 19" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" fill="none" />
      {/* Highlight on flap */}
      <path d="M10 14H42C44.5 14 46 16 46 18L26 30L6 18C6 16 7.5 14 10 14Z" fill="rgba(255,255,255,0.12)" />
      {/* Paper peeking */}
      <rect x="12" y="11" width="28" height="8" rx="2" fill="white" />
      <rect x="16" y="14" width="12" height="1.5" rx="0.75" fill="#E2E8F0" />
      <rect x="16" y="17" width="8" height="1.5" rx="0.75" fill="#E2E8F0" />
      {/* Small seal/badge */}
      <circle cx="37" y="11" r="5" fill="#F59E0B" />
      <circle cx="37" cy="11" r="5" fill="#F59E0B" />
      <path d="M35 11L36.5 12.5L39.5 9.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <defs>
        <linearGradient id="mailGrad" x1="26" y1="14" x2="26" y2="42" gradientUnits="userSpaceOnUse">
          <stop stopColor="#5AABF7" />
          <stop offset="1" stopColor="#3B82F6" />
        </linearGradient>
        <linearGradient id="flapGrad" x1="26" y1="14" x2="26" y2="32" gradientUnits="userSpaceOnUse">
          <stop stopColor="#7CC4FA" />
          <stop offset="1" stopColor="#4A9EF5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function Contact() {
  return (
    <section className="contact" id="contact">
      <motion.div
        className="contact-card"
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <motion.div
          className="contact-icon"
          initial={{ opacity: 0, scale: 0.7 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.15 }}
        >
          <MailIcon />
        </motion.div>

        <motion.h2
          className="contact-heading"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
        >
          Let's connect
        </motion.h2>

        <motion.p
          className="contact-subtitle"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
        >
          I'd love to hear what you're building. Whether it's a collaboration,
          a product idea, or just a conversation — reach out below to start the convo.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.4 }}
        >
          <MagneticButton href="mailto:contact@leoamiri.com" className="contact-btn">
            Reach out
          </MagneticButton>
        </motion.div>
      </motion.div>

      <motion.p
        className="contact-location"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.5 }}
      >
        I'm located in Katowice, Poland timezone (CET).<br />
        I'll get back to you as soon as I can.
      </motion.p>

      <motion.p
        className="contact-signature"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
      >
        Cheers,<br />
        Leo Amiri
      </motion.p>
    </section>
  );
}
